import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Trash2 } from 'lucide-react'
import Modal from '../../../ui/Modal'

export interface NodeDeleteConfirmationItem {
  id: string
  label: string
  type: string | undefined
}

interface NodeDeleteConfirmationDialogProps {
  isOpen: boolean
  nodes: NodeDeleteConfirmationItem[]
  onCancel: () => void
  onConfirm: () => void
}

export const NodeDeleteConfirmationDialog = memo(function NodeDeleteConfirmationDialog({
  isOpen,
  nodes,
  onCancel,
  onConfirm,
}: NodeDeleteConfirmationDialogProps) {
  const { t } = useTranslation('canvas')
  const count = nodes.length
  const title = count === 1 ? t('nodeDeleteConfirmationDialog.titleSingle') : t('nodeDeleteConfirmationDialog.titlePlural', { count })
  const nodeNames = nodes.map((node) => node.label).join(', ')

  return (
    <Modal isOpen={isOpen} onClose={onCancel} title={title} size="sm">
      <div data-testid="node-delete-confirmation-dialog" className="space-y-4">
        <div className="flex gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
            <Trash2 className="h-4 w-4" />
          </div>
          <div className="min-w-0 space-y-1">
            <p className="text-sm text-foreground">
              {count === 1
                ? <>{t('nodeDeleteConfirmationDialog.deleteNode', { nodeNames })}</>
                : <>{t('nodeDeleteConfirmationDialog.deleteNodes', { count })}</>}
            </p>
            <p className="text-sm text-muted-foreground">
              {t('nodeDeleteConfirmationDialog.connectedEdgesRemoved')}
            </p>
            {count > 1 && (
              <p className="truncate text-xs font-medium text-text-muted" title={nodeNames}>
                {nodeNames}
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            data-testid="node-delete-confirmation-cancel"
            onClick={onCancel}
            className="h-9 rounded-lg border border-input-border bg-surface-1 px-3 text-sm font-medium text-foreground hover:border-input-border-hover hover:bg-surface-3"
          >
            {t('nodeDeleteConfirmationDialog.cancel')}
          </button>
          <button
            type="button"
            data-testid="node-delete-confirmation-confirm"
            onClick={onConfirm}
            className="h-9 rounded-lg bg-destructive px-3 text-sm font-medium text-white hover:bg-destructive/90"
          >
            {t('nodeDeleteConfirmationDialog.delete')}
          </button>
        </div>
      </div>
    </Modal>
  )
})

NodeDeleteConfirmationDialog.displayName = 'NodeDeleteConfirmationDialog'
